import React, { useState, useRef } from 'react';
import { FiEdit, FiCheck, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { resumeApi } from '../../services/api';

const ResumeNameEditor = ({ resumeId, initialName, onUpdateSuccess, className = "" }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(initialName || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  const handleEdit = () => {
    setIsEditing(true);
    // Focus the input after rendering
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 10);
  };

  const handleCancel = () => {
    setName(initialName);
    setIsEditing(false);
  };

  const handleSubmit = async () => {
    if (name.trim() === '') {
      toast.error('Resume name cannot be empty');
      return;
    }

    if (name === initialName) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSubmitting(true);
      await resumeApi.updateResumeMetadata(resumeId, { name });
      toast.success('Resume name updated successfully');
      onUpdateSuccess(name);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating resume name:', error);
      toast.error('Failed to update resume name');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    const inputClasses = className.includes('text-sm') 
      ? "py-1 px-2 text-sm font-medium border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500 bg-white"
      : "py-1 px-2 text-xl font-semibold border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500 bg-white";
    
    return (
      <div className={`flex items-center ${className}`}>
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          className={inputClasses}
          disabled={isSubmitting}
        />
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="ml-2 text-green-600 hover:text-green-800"
          title="Save"
        >
          <FiCheck className="h-5 w-5" />
        </button>
        <button
          onClick={handleCancel}
          disabled={isSubmitting}
          className="ml-1 text-red-600 hover:text-red-800"
          title="Cancel"
        >
          <FiX className="h-5 w-5" />
        </button>
      </div>
    );
  }

  const titleClasses = className.includes('text-sm') 
    ? "text-sm font-medium text-primary-600 cursor-pointer hover:text-primary-800 truncate"
    : "text-xl font-semibold text-gray-800 cursor-pointer hover:text-primary-600 truncate max-w-xs";
  
  const iconClasses = className.includes('text-sm') 
    ? "h-3 w-3"
    : "h-4 w-4";

  return (
    <div className={`flex items-center ${className}`}>
      <span 
        className={titleClasses}
        onClick={handleEdit} 
        title="Click to edit resume name"
      >
        {initialName || "Untitled Resume"}
      </span>
      <button
        onClick={handleEdit}
        className="ml-2 text-gray-400 hover:text-gray-600"
        title="Edit resume name"
      >
        <FiEdit className={iconClasses} />
      </button>
    </div>
  );
};

export default ResumeNameEditor;